/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiFlexItem } from '@elastic/eui'

import { useGetCCMClusters } from '@modules/ccm-user-lib/hooks'
import type { RoleAssignments } from '@modules/cloud-api/v1/types'
import { useOrganizationId } from '@modules/permissions-lib/hooks'
import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'

import CCMRolePanel from './CCMRolePanel'
import {
  getRoleSelectOptionValue,
  roleAssignmentsToCCMRoleAssignmentAdapter,
  transformRoleAssignments,
} from './lib'

import type { FC } from 'react'
import type { CCMRole, CCMRoleAssignment } from './types'

type ConnectedCCMRolePanelProps = {
  roleAssignments: RoleAssignments
  initiallyChecked: boolean
  roleSelectionOverride: boolean
  onChangeRoleAssignments: (roleAssignments: RoleAssignments) => void
  onSwitchToggle: () => void
}

const ConnectedCCMRolePanel: FC<ConnectedCCMRolePanelProps> = ({
  roleSelectionOverride,
  initiallyChecked,
  roleAssignments,
  onChangeRoleAssignments,
  onSwitchToggle,
}) => {
  const isCCMEnabled = isCCMFeatureActivated()
  const {
    data,
    isSuccess: clusterSuccessfullyFetched,
    isLoading: isLoadingCCMClusters,
  } = useGetCCMClusters({
    enabled: isCCMEnabled,
  })

  const { isOrganizationIdFetched, organizationId } = useOrganizationId({
    enabled: isCCMEnabled,
  })

  if (!isCCMEnabled) {
    return null
  }

  const isNotReadyForHandlingEvents = !clusterSuccessfullyFetched || !isOrganizationIdFetched

  const allClustersRoleSelection = roleAssignments.cloud_connected_resource?.find(
    (assignment) => assignment.all,
  )

  const uiRoleAssignments = clusterSuccessfullyFetched
    ? roleAssignmentsToCCMRoleAssignmentAdapter(roleAssignments, data.items)
    : []

  function onChangeRoleForAllConnectedClusters(role: CCMRole): void {
    if (isNotReadyForHandlingEvents) {
      return
    }

    onChangeRoleAssignments({
      ...roleAssignments,
      cloud_connected_resource: [
        {
          all: true,
          role_id: role,
          organization_id: organizationId ?? '', // organizationId should be defined here or else it'd have returned above
        },
      ],
    })
  }

  function onChangeRoleForConnectedCluster(uiRoleAssignment: CCMRoleAssignment): void {
    if (isNotReadyForHandlingEvents) {
      return
    }

    onChangeRoleAssignments(
      transformRoleAssignments(roleAssignments, uiRoleAssignment, organizationId ?? ''),
    )
  }

  function onUnassignRoleForAllConnectedClusters(): void {
    if (isNotReadyForHandlingEvents) {
      return
    }

    onChangeRoleAssignments({
      ...roleAssignments,
      cloud_connected_resource: [],
    })
  }

  return (
    <EuiFlexItem>
      <CCMRolePanel
        isChecked={initiallyChecked}
        roleSelectionOverride={roleSelectionOverride}
        selectedRoleForAllDeployments={getRoleSelectOptionValue(allClustersRoleSelection?.role_id)}
        roleAssignments={uiRoleAssignments}
        onAssignRoleForAllConnectedClusters={onChangeRoleForAllConnectedClusters}
        onUnassignRoleForAllConnectedClusters={onUnassignRoleForAllConnectedClusters}
        onChangeRoleForConnectedCluster={onChangeRoleForConnectedCluster}
        isLoading={isLoadingCCMClusters}
        onSwitchToggle={onSwitchToggle}
      />
    </EuiFlexItem>
  )
}

export default ConnectedCCMRolePanel
